import { UserCoreService } from './user/userCoreService';
import { UserAccessService } from './user/userAccessService';
import { UserReferentsService } from './user/userReferentsService';
import { UserTicketsService } from './user/userTicketsService';

/**
 * UserService : orchestrateur qui combine plusieurs sous-services pour respecter
 * la limite de 500 lignes par fichier.
 *
 * Sous-services :
 *  - UserCoreService      : CRUD utilisateurs + rôles + reset password
 *  - UserAccessService    : accès panel + IP history + online + force logout +
 *                            role default access
 *  - UserReferentsService : référents (types, définitions, assignations,
 *                            codes d'invitation)
 *  - UserTicketsService   : tickets équipe + clear-auth-rate-limit
 *
 * Toutes les méthodes publiques restent disponibles via `userService.xxx()` —
 * comportement identique à l'ancienne implémentation monolithique.
 */
class UserService {
  private core = new UserCoreService();
  private access = new UserAccessService();
  private referents = new UserReferentsService();
  private tickets = new UserTicketsService();

  // — Core (users + rôles + reset password) —
  getUsers = this.core.getUsers.bind(this.core);
  getUserById = this.core.getUserById.bind(this.core);
  getRoles = this.core.getRoles.bind(this.core);
  updateUserRole = this.core.updateUserRole.bind(this.core);
  updateUserAccess = this.core.updateUserAccess.bind(this.core);
  getUserRoleHistory = this.core.getUserRoleHistory.bind(this.core);
  resetUserPassword = this.core.resetUserPassword.bind(this.core);

  // — Access (panel + IP + online + logout + role default access) —
  getUserPanelAccess = this.access.getUserPanelAccess.bind(this.access);
  updateUserPanelAccess = this.access.updateUserPanelAccess.bind(this.access);
  getPanelSections = this.access.getPanelSections.bind(this.access);
  getUserIpHistory = this.access.getUserIpHistory.bind(this.access);
  getOnlineUsers = this.access.getOnlineUsers.bind(this.access);
  forceLogoutUser = this.access.forceLogoutUser.bind(this.access);
  getRoleDefaultAccess = this.access.getRoleDefaultAccess.bind(this.access);
  getAllRolesDefaultAccess = this.access.getAllRolesDefaultAccess.bind(this.access);
  updateRoleDefaultAccess = this.access.updateRoleDefaultAccess.bind(this.access);

  // — Référents (types, définitions, assignations, codes d'invitation) —
  getMyReferents = this.referents.getMyReferents.bind(this.referents);
  getUserReferents = this.referents.getUserReferents.bind(this.referents);
  assignReferent = this.referents.assignReferent.bind(this.referents);
  removeReferent = this.referents.removeReferent.bind(this.referents);
  getReferentTypes = this.referents.getReferentTypes.bind(this.referents);
  createReferentType = this.referents.createReferentType.bind(this.referents);
  updateReferentType = this.referents.updateReferentType.bind(this.referents);
  deactivateReferentType = this.referents.deactivateReferentType.bind(this.referents);
  getReferentDefinitions = this.referents.getReferentDefinitions.bind(this.referents);
  createReferentDefinition = this.referents.createReferentDefinition.bind(this.referents);
  updateReferentDefinition = this.referents.updateReferentDefinition.bind(this.referents);
  deactivateReferentDefinition = this.referents.deactivateReferentDefinition.bind(this.referents);
  getReferentFunctions = this.referents.getReferentFunctions.bind(this.referents);
  getInvitationCodes = this.referents.getInvitationCodes.bind(this.referents);
  generateInvitationCodes = this.referents.generateInvitationCodes.bind(this.referents);

  // — Tickets équipe + reset rate-limit —
  getPanelTeamTickets = this.tickets.getPanelTeamTickets.bind(this.tickets);
  createPanelTeamTicket = this.tickets.createPanelTeamTicket.bind(this.tickets);
  updatePanelTeamTicketStatus = this.tickets.updatePanelTeamTicketStatus.bind(this.tickets);
  clearAuthRateLimit = this.tickets.clearAuthRateLimit.bind(this.tickets);
}

// Re-exports des types (préserve l'API publique du fichier d'origine)
export type {
  Role,
  UserWithRole,
  UsersResponse,
  RoleHistory,
  PanelTeamTicket,
  UserIpHistory,
  PanelAccess,
  PanelSection,
  Referent,
  ReferentFunction,
  ReferentTypeDefinition,
  ReferentFunctionDefinition,
  InvitationCodeRow,
  RoleDefaultAccess,
} from './user/types';

const userService = new UserService();
export default userService;
